import React, { Suspense, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  Bell,
  User,
  LogOut,
  Moon,
  Sun,
  HelpCircle,
  Bug,
  SlidersHorizontal
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useNotificacoesFinanceiras } from '../../hooks/useNotificacoesFinanceiras'
import { usePageHeaderStore } from '../../stores/pageHeaderStore'
import { useThemeStore } from '../../stores/themeStore'
import { useScaleStore, UIScale } from '../../stores/scaleStore'
import { useLocation } from 'react-router-dom'
import NotificacoesDropdown from './NotificacoesDropdown'

const QuickReportModal = React.lazy(() => import('./QuickReportModal'))
const PerfilUsuarioModal = React.lazy(() => import('./PerfilUsuarioModal'))

export default function ModernHeader() {
  const [notificacoesOpen, setNotificacoesOpen] = useState(false)
  const bellButtonRef = useRef<HTMLButtonElement>(null)
  const { userData, logout } = useAuth()
  const { totalCount } = useNotificacoesFinanceiras()
  const pageTitle = usePageHeaderStore((s) => s.title)
  const pageSubtitle = usePageHeaderStore((s) => s.subtitle)
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const initTheme = useThemeStore((s) => s.initTheme)
  const [quickReportOpen, setQuickReportOpen] = useState(false)
  const [perfilOpen, setPerfilOpen] = useState(false)
  const [scaleOpen, setScaleOpen] = useState(false)
  const [scalePos, setScalePos] = useState({ top: 0, right: 0 })
  const scaleRef = useRef<HTMLDivElement>(null)
  const scaleBtnRef = useRef<HTMLButtonElement>(null)
  const { scale, setScale, initScale } = useScaleStore()
  const location = useLocation()

  // Sincroniza DOM com o valor persistido do store apos rehydrate
  useEffect(() => {
    initTheme()
    initScale()
  }, [initTheme, initScale])

  // Fechar dropdown de escala ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        scaleRef.current && !scaleRef.current.contains(e.target as Node) &&
        scaleBtnRef.current && !scaleBtnRef.current.contains(e.target as Node)
      ) {
        setScaleOpen(false)
      }
    }
    if (scaleOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [scaleOpen])

  const handleScaleOpen = useCallback(() => {
    if (scaleBtnRef.current) {
      const rect = scaleBtnRef.current.getBoundingClientRect()
      setScalePos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setScaleOpen(prev => !prev)
  }, [])

  return (
    <>
    <header className="header-compact h-16 bg-surface/80 backdrop-blur-xl border-b border-border-primary shadow-lg shadow-black/5">
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section — Titulo da pagina (publicado via usePageHeader) */}
        <div className="min-w-0 flex-1 mr-4">
          {pageTitle && (
            <>
              <h1 className="text-lg font-bold text-text-primary leading-tight truncate">
                {pageTitle}
              </h1>
              {pageSubtitle && (
                <p className="text-xs text-text-muted truncate leading-tight">
                  {pageSubtitle}
                </p>
              )}
            </>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          {/* Quick Actions */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Help - Iniciar Tour da Página */}
            <button
              onClick={() => window.dispatchEvent(new Event('tour:start'))}
              className="header-action-btn p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group relative"
              title="Ver tour da página"
            >
              <HelpCircle className="w-5 h-5 text-text-secondary group-hover:text-primary-accent" />
            </button>

            {/* Report Bug */}
            <button
              data-tour="btn-bug"
              onClick={() => setQuickReportOpen(true)}
              className="header-action-btn p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group relative"
              title="Reportar Ocorrência"
            >
              <Bug className="w-5 h-5 text-text-secondary group-hover:text-orange-400" />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              className="header-action-btn p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group relative"
              title={resolvedTheme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="w-5 h-5 text-text-secondary group-hover:text-primary-accent" />
              ) : (
                <Moon className="w-5 h-5 text-text-secondary group-hover:text-primary-accent" />
              )}
            </button>

            {/* Scale Selector */}
            <div className="relative">
              <button
                ref={scaleBtnRef}
                onClick={handleScaleOpen}
                className="header-action-btn p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group relative"
                title={`Escala da interface: ${scale}%`}
              >
                <SlidersHorizontal className="w-5 h-5 text-text-secondary group-hover:text-primary-accent" />
                {scale !== 100 && (
                  <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-primary-500 text-white rounded-full w-4 h-4 flex items-center justify-center leading-none">
                    {scale}
                  </span>
                )}
              </button>

              {scaleOpen && createPortal(
                <div
                  ref={scaleRef}
                  style={{ position: 'fixed', top: scalePos.top, right: scalePos.right, zIndex: 9999 }}
                  className="w-40 bg-surface border border-border-primary rounded-xl shadow-xl shadow-black/20 overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-border-primary">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Tamanho da Tela</p>
                  </div>
                  {([80, 85, 90, 95, 100] as UIScale[]).map((value) => (
                    <button
                      key={value}
                      onClick={() => { setScale(value); setScaleOpen(false) }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-surface-light ${
                        scale === value ? 'text-primary-accent font-semibold' : 'text-text-primary'
                      }`}
                    >
                      <span>{value}%</span>
                      {scale === value && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                      )}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="header-divider hidden md:block w-px h-6 bg-border-primary" />

          {/* Notifications */}
          <button
            ref={bellButtonRef}
            onClick={() => setNotificacoesOpen(prev => !prev)}
            className="header-action-btn relative p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group"
          >
            <Bell className="w-5 h-5 text-text-secondary group-hover:text-primary-accent" />
            {totalCount > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-error-500 to-error-600 rounded-full flex items-center justify-center animate-pulse">
                <span className="text-xs font-bold text-white">
                  {totalCount > 9 ? '9+' : totalCount}
                </span>
              </div>
            )}
          </button>
          <NotificacoesDropdown
            isOpen={notificacoesOpen}
            onClose={() => setNotificacoesOpen(false)}
            anchorRef={bellButtonRef}
          />

          {/* User Menu */}
          <div className="relative">
            <button onClick={() => setPerfilOpen(true)} className="flex items-center space-x-3 p-2 rounded-xl hover:bg-surface-light transition-all duration-200 group">
              <div className="header-avatar w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-xs font-bold">
                  {userData?.nome ? userData.nome.substring(0, 2).toUpperCase() : <User className="w-4 h-4" />}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-text-primary">
                  {userData?.nome || 'Admin User'}
                </p>
                <p className="text-xs text-text-muted">
                  {userData?.email || 'Administrador'}
                </p>
              </div>
            </button>
          </div>

          {/* Logout */}
          <button
            className="header-action-btn p-2 rounded-xl hover:bg-error-500/10 transition-all duration-200 group"
            onClick={logout}
            title="Sair do sistema"
          >
            <LogOut className="w-5 h-5 text-text-secondary group-hover:text-error-500" />
          </button>
        </div>
      </div>
    </header>
    <Suspense fallback={null}>
      <QuickReportModal
        isOpen={quickReportOpen}
        onClose={() => setQuickReportOpen(false)}
        currentRoute={location.pathname}
      />
    </Suspense>
    <Suspense fallback={null}>
      <PerfilUsuarioModal
        isOpen={perfilOpen}
        onClose={() => setPerfilOpen(false)}
      />
    </Suspense>
    </>
  )
}